/*
  Every word the client register and profile say, in one place, so the screen files read as layout
  and the wording can be reviewed without reading React. The prototype is the source for the words
  it has; the ones it does not have are written to say the same thing.
*/

import type { StylingSessionStatus } from '@/features/customers/api';

/**
 * The record's four tabs, in the order the design draws them. Exported because the route
 * validates `?tab=` against this same list, so a tab cannot exist in one place and not the other.
 */
export const CUSTOMER_TABS = ['profile', 'money', 'wardrobe', 'styling'] as const;

export type CustomerTab = (typeof CUSTOMER_TABS)[number];

export const COPY = {
    /*
      The nav group the register sits in, as every Configuration page says. The prototype's "The A
      List" is from before Customers moved under Configuration (ALS-287).
    */
    eyebrow: 'Configuration',
    title: 'Customers',
    cardTitle: 'Customers',
    add: 'Add customer',
    searchLabel: 'Search customers by name, email or mobile',
    count: (shown: number, total: number) =>
        shown === total
            ? `${total} ${total === 1 ? 'customer' : 'customers'}`
            : `${shown} of ${total}`,
    columns: {
        customer: 'Customer',
        country: 'Country',
        manager: 'RM',
        clientType: 'Client type',
        membership: 'Membership',
        lifetime: 'Lifetime spend',
        wallet: 'Wallet',
        status: 'Status',
    },
    /** The wallet column reads as "who holds credit", so nothing reads as a dash rather than £0. */
    noWallet: '—',
    noClientType: '—',
    filters: {
        manager: 'Filter customers by relationship manager',
        country: 'Filter customers by country',
        status: 'Filter customers by status',
        allManagers: 'All RMs',
        allCountries: 'All countries',
        allStatuses: 'All statuses',
        deactivated: 'Deactivated',
    },
    record: {
        breadcrumb: 'Customers',
        save: 'Save changes',
        close: 'Close',
        unsaved: 'Unsaved changes',
        subtitleFallback: 'Client profile',
        /** A role that may look at clients but not change them (Limited Access). */
        viewOnly: 'View only',
        viewOnlyNote: 'Your role can look at clients but not change them.',
        /** What the stars stand for, so a masked value is not read as the client's data (ALS-482). */
        masked: 'Email, phone, addresses and spend are masked for your role.',
        /** A masked figure, drawn as the server masks an address: the old portal's stars. */
        maskedValue: '**********',
        missing: {
            // Verbatim from the prototype's copy.missingCustomerRecord (scripts/data.js).
            title: 'This customer could not be found',
            body: 'It may have been deleted, or the link may be out of date.',
            action: 'Back to customers',
        },
        leave: {
            title: 'Leave without saving?',
            body: 'Profile changes on this page are not saved yet.',
            confirm: 'Leave',
            cancel: 'Stay',
        },
        tabs: {
            profile: 'Profile',
            money: 'Wallet & orders',
            wardrobe: 'Wardrobe',
            styling: 'Styling',
        },
    },
    /*
      The Wallet & orders tab's lower half (ALS-169): the Orders register's table, this client
      only. The words are the prototype's (scripts/screens/customer-detail.js); the table's own
      come from the orders feature, so the two screens cannot word a column differently.
    */
    orders: {
        statusLabel: 'Filter by order status',
        statusOption: (status: string) => `Order status: ${status}`,
        all: 'All',
        /**
         * In the card's header whatever the count, and in the empty state too (ALS-496): New order
         * with this client already picked, so a second order is not a trip through the register.
         */
        newOrder: 'New order',
        /** The footer: the register's range, and the status when one is chosen. */
        foot: (range: string, status: string | null) =>
            status ? `${range} · ${status.toLowerCase()}` : range,
        empty: {
            title: 'No orders yet',
            body: 'Orders placed for this client appear here.',
        },
        /** The chosen status has nothing, although the client has orders. */
        noneInStatus: {
            title: (status: string) => `No ${status.toLowerCase()} orders`,
            body: (total: number, status: string) =>
                `None of this client\u2019s ${total.toLocaleString('en-GB')} ${total === 1 ? 'order is' : 'orders are'} ${status.toLowerCase()}.`,
            action: 'Show all statuses',
        },
        /** In the card's place for a role without orders (ALS-523), in the words of a page it cannot open. */
        noAccess: {
            title: 'Your role does not include orders',
            body: 'An Admin can change your role on the Users screen if you need it.',
        },
    },
    /*
      The Wallet tab (ALS-51). The words are the prototype's where it has them; the drawer's follow
      the direction, which is the ticket's own requirement, so nobody reads "Add funds" while about
      to take money off a client.
    */
    wallet: {
        title: 'Wallet',
        adjust: 'Adjust funds',
        /** Why Adjust funds is refused: the role, or the person's own Add fund to wallet switch. */
        refusedRole: 'Your role can look at clients but not change them.',
        refusedSwitch:
            'Your account cannot adjust wallets. An Admin can switch on Add fund to wallet.',
        empty: 'No wallet movements',
        emptyBody: 'Funds added, credits and amounts applied to orders appear here.',
        /** The rule, on the card, because it is the reason there is no edit button on a row. */
        foot: (entries: number) =>
            `${entries} ${entries === 1 ? 'entry' : 'entries'} · every movement is recorded and cannot be edited`,
        figures: {
            balance: 'Available balance',
            balanceNote: (on: string | null) => (on ? `Added ${on}` : 'No funds added yet'),
            awaiting: 'Awaiting payment',
            applied: 'Applied to orders',
            appliedNote: (orders: number) =>
                orders === 0
                    ? 'No credit used yet'
                    : `Across ${orders} ${orders === 1 ? 'order' : 'orders'}`,
        },
        columns: {
            date: 'Date',
            description: 'Description',
            type: 'Type',
            debit: 'Debit',
            credit: 'Credit',
            balance: 'Balance',
        },
        bankTransfer: 'Bank transfer',
        /** What a foreign entry says under its description, so the pound figure can be checked. */
        converted: (amount: string, rate: number) => `${amount} at ${rate}`,
        addedBy: (who: string | null) => (who ? `Added by ${who}` : null),
        fromOrder: (orderNo: number) => `Order ${orderNo}`,
        none: '—',
        drawer: {
            title: 'Adjust wallet',
            subtitle: (name: string) => `${name} · every movement is recorded in the passbook`,
            note: 'A wallet entry cannot be edited or deleted once saved.',
            direction: 'Direction',
            topup: 'Topup',
            deduct: 'Deduct',
            amount: 'Amount',
            currency: 'Currency',
            rate: 'Exchange rate',
            /** The pound figure the entry will carry, shown while it is being typed. */
            converted: (amount: string) => `Recorded as ${amount}`,
            bankTransfer: 'Bank transfer',
            bankTransferNote: 'Marks the passbook entry as a bank transfer receipt.',
            description: 'Description',
            descriptionPlaceholder: (creditNote: boolean) =>
                creditNote
                    ? 'Why the wallet is changing. The client sees this in the app and on the credit note.'
                    : 'Why the wallet is changing. The client sees this in the app.',
            cancel: 'Cancel',
            /** Everything below follows the direction. */
            save: (deduct: boolean) => (deduct ? 'Deduct funds' : 'Add funds'),
            saving: (deduct: boolean) => (deduct ? 'Deducting…' : 'Adding…'),
            done: (deduct: boolean, amount: string) =>
                deduct ? `Funds deducted · ${amount}` : `Funds added · ${amount}`,
            failed: (deduct: boolean) =>
                deduct ? 'That deduction could not be saved.' : 'Those funds could not be added.',
            amountRequired: 'A number above zero.',
            amountInvalid: 'An amount above zero, to two decimal places.',
            rateRequired: 'A rate above zero.',
            /** The message names the balance, because that is the number the stylist needs. */
            overBalance: (available: string) =>
                `More than the balance. ${available} is available to deduct.`,
            required: 'Required.',
        },
    },
    figures: {
        wallet: 'Wallet balance',
        walletNote: (on: string | null) => (on ? `Added ${on}` : 'No funds added yet'),
        lifetime: 'Lifetime spend',
        lifetimeNote: (orders: number) => `Across ${orders} ${orders === 1 ? 'order' : 'orders'}`,
        awaiting: 'Awaiting payment',
        awaitingNote: (orders: number) =>
            orders === 0
                ? 'Nothing outstanding'
                : `${orders} ${orders === 1 ? 'order unpaid' : 'orders unpaid'}`,
        since: 'Client since',
    },
    details: {
        title: 'Client details',
        subtitle: 'name, contact and where they are',
        firstName: 'First name',
        middleName: 'Middle name',
        lastName: 'Last name',
        gender: 'Gender',
        birthday: 'Birthday',
        birthdayHint: 'Day and month, as the client gives it: "21 December".',
        email: 'Email',
        mobile: 'Mobile',
        mobilePlaceholder: '+44 …',
        country: 'Country',
        city: 'City',
        socials: 'Socials',
        addSocial: 'Add',
        socialPlaceholder: '@username',
        changePhoto: 'Change photo',
        removePhoto: 'Remove photo',
        photoUploading: 'Uploading\u2026',
        photoFailed: 'That photo could not be uploaded.',
        /* Refused before it is sent, in the wardrobe photo field's words (ALS-217). */
        photoTooLarge: (limitMb: number) => `That photo is larger than ${limitMb} MB.`,
        photoAlt: (name: string) => `${name}'s photo`,
        required: 'Required.',
        emailInvalid: 'Enter a valid email address.',
        mobileInvalid: 'Enter a valid phone number.',
        birthdayInvalid: 'Give a day and a month, like "21 December".',
    },
    membership: {
        title: 'Membership, access & team',
        subtitle: 'what the client gets and who owns them',
        clientType: 'Client type',
        clientTypeHint: 'A client can be more than one.',
        chat: 'Customer chat',
        membership: 'Membership',
        membershipEnds: 'Membership ends',
        // The rule, not a claim about what just happened: the field shows the same hint on a page
        // that has only been opened, where nothing was set from today at all.
        membershipEndsHint: (name: string, months: number) =>
            `Choosing ${name} sets this ${months} months from today. It can be changed.`,
        status: 'Status',
        manager: 'Relationship manager',
        managerHint: 'Changing this records the handover on the client.',
        noManagers:
            'No account has the relationship manager switch on yet. Turn it on for somebody under Configuration, Relationship managers.',
        location: 'Location',
        appLogin: 'App login',
        canSignIn: 'Can sign in',
        resetPassword: 'Reset password',
        /*
          The client's password is the app's, not this portal's: the mobile API emails a
          verification code the app then asks for (UserRepository.ForgotPassword), and the admin
          cannot start that flow without the app. Saying so is better than a button that looks as
          though it did something.
        */
        /*
          A client has one password across the app, the old portal and this admin (ADR 0021), so
          this link sets the one they use everywhere. It does not let them in here: completing it
          grants no session, and the login still refuses a client.
        */
        resetConfirm: (name: string) =>
            `Email ${name} a link to choose a new password? It works in the client app and on the old portal, and it does not give them access to this admin.`,
        resetSend: 'Send the link',
        resetSent: (email: string) => `Link sent · ${email}`,
        resetFailed: 'That link could not be sent.',
        remarks: 'Internal remarks',
        remarksPlaceholder: 'Business notes, never shown to the client.',
    },
    comments: {
        title: 'Comments',
        count: (shown: number, total: number) =>
            total === 0
                ? 'internal, never shown to the client'
                : shown === total
                  ? `${total} ${total === 1 ? 'comment' : 'comments'} · internal, never shown to the client`
                  : `${shown} of ${total} · internal, never shown to the client`,
        placeholder: 'Add a note for the team…',
        add: 'Add comment',
        label: 'New comment',
        empty: 'No comments',
        emptyBody: 'Notes the team leaves on this client appear here, newest first.',
        required: 'Write the comment first.',
        failed: 'That comment could not be added.',
        attach: 'Attach files',
        attachHint: 'PDF or an image, up to 10 MB each.',
        remove: (name: string) => `Remove ${name}`,
        /* The note landed; the file did not. Both halves are said, because a message that only
           named the failure would read as though the comment had been lost too. */
        filesFailed: (names: string[]) =>
            names.length === 1
                ? `Comment added, but ${names[0]} could not be attached.`
                : `Comment added, but ${names.length} files could not be attached.`,
        fileRemoveFailed: 'That file could not be removed.',
        legacyFile: 'On the old portal until the files are moved across.',
    },
    addresses: {
        title: 'Addresses',
        count: (n: number) => (n === 1 ? '1 address' : `${n} addresses`),
        add: 'Add',
        empty: 'No addresses recorded.',
        emptyNote: 'Add the homes and offices parcels go to.',
        default: 'Default',
        newTitle: 'Add address',
        editTitle: 'Edit address',
        recipient: 'Addressed to',
        line1: 'Address',
        line2: 'Address line 2',
        postcode: 'Postcode',
        town: 'City',
        region: 'Region',
        country: 'Country',
        mobile: 'Phone',
        isDefault: 'Default address',
        isDefaultHint: 'Where a parcel goes and the invoice is addressed when nobody picks.',
        remove: (what: string) => `Remove ${what}?`,
        removeBody: 'The address comes off the client. Orders already sent to it are untouched.',
        removeConfirm: 'Remove',
    },
    staff: {
        title: 'Personal staff',
        count: (n: number) => (n === 1 ? '1 person' : `${n} people`),
        add: 'Add',
        empty: 'No staff recorded.',
        emptyNote: 'Assistants and drivers can hold their own app logins.',
        newTitle: 'Add staff member',
        editTitle: 'Edit staff member',
        name: 'Full name',
        type: 'Type',
        email: 'Email',
        mobile: 'Phone',
        appLogin: 'App login',
        appLoginHint: 'Staff with app login act on the client account.',
        notes: 'Remarks',
        remove: (what: string) => `Remove ${what}?`,
        removeBody: 'They come off the client and their own app login is closed.',
        removeConfirm: 'Remove',
    },
    rmHistory: {
        title: 'RM history',
        count: (n: number) => (n === 0 ? 'no changes yet' : n === 1 ? '1 change' : `${n} changes`),
        empty: (manager: string | null, since: string) =>
            manager
                ? `${manager} has owned this client since ${since}.`
                : `Nobody has been recorded as owning this client since ${since}.`,
        emptyNote: 'Reassignments are recorded here automatically.',
        changedBy: (who: string | null, when: string) =>
            who ? `Changed by ${who} · ${when}` : `Changed ${when}`,
    },
    /*
      The Styling tab (ALS-50). The prototype's own words where it has them; the rest written to
      say the same thing.
    */
    /** The Allergies card and the strip that reads it (ALS-393), copy from the mock-ups. */
    allergies: {
        title: 'Allergies',
        subtitle: 'shown on every tab of this profile, on New order and on her orders',
        none: 'none recorded',
        add: 'Add',
        addLabel: 'Add an allergy',
        placeholder: 'Add an allergy, e.g. Mohair, nickel, latex',
        remove: (name: string) => `Remove ${name}`,
        note: 'What the team should do',
        edited: 'Edited · saves with the profile',
        /** "ALLERGY · MOHAIR", or "ALLERGIES · MOHAIR, NICKEL" for more than one. */
        stripTitle: (names: readonly string[]) =>
            `${names.length === 1 ? 'Allergy' : 'Allergies'} · ${names.join(', ')}`,
        edit: 'Edit',
        viewProfile: 'View profile',
    },
    sizing: {
        title: 'Sizing',
        subtitle: 'what the stylist needs to pull the right pieces',
        clothing: 'Clothing',
        body: 'Body',
        edited: 'Edited · saves with the profile',
        empty: 'Nothing measured yet.',
        emptyNote: 'Sizes save with the profile, so fill in what you know and press Save changes.',
    },
    rings: {
        title: 'Ring sizes',
        subtitle: 'one size per finger, per hand',
        notes: 'Sizing notes',
        notesPlaceholder: 'Fit preferences, alterations, anything a stylist should know.',
        /** Spoken, for the ten boxes: a grid of bare inputs needs a label each. */
        finger: (hand: string, finger: string) => `${hand} ${finger}`,
    },
    specialDates: {
        title: 'Special dates',
        count: (n: number) => (n === 1 ? '1 date' : `${n} dates`),
        add: 'Add',
        empty: 'No special dates yet.',
        emptyNote: 'Occasions feed the wear-date reminders.',
        newTitle: 'New special date',
        editTitle: 'Edit special date',
        forClient: (name: string) => `For ${name}`,
        occasion: 'Occasion',
        occasionPlaceholder: 'e.g. Wedding anniversary',
        date: 'Date',
        relation: 'Reference',
        relationPlaceholder: 'Whose date is it?',
        reminder: 'Reminder',
        remind: 'Remind the relationship manager',
        remindHint: 'Two weeks before, so there is time to prepare something.',
        /** Said plainly: the switch records what was asked for, and nothing sends it yet. */
        remindNote: 'The reminder is recorded on the client. Sending it is not built yet.',
        /**
         * A note about special dates in general, and the design puts it at the end of the
         * dialog rather than under a field. It sat under Reference while describing the Date
         * (ALS-188). There is no recurring control because there is no recurrence to set:
         * `dbo.CustomerSpecialDates` has no such column (ADR 0039), a date simply comes round.
         */
        recurring: 'Recurring dates repeat every year on the same day.',
        save: 'Add date',
        saveEdit: 'Save date',
        remove: (what: string) => `Remove ${what}?`,
        removeBody: 'The date comes off the client. Orders already placed for it are untouched.',
        removeConfirm: 'Remove',
        /** Suggestions, not a register: the legacy column is free text and blank on every row. */
        relations: [
            'Self',
            'Husband',
            'Wife',
            'Daughter',
            'Son',
            'Mother',
            'Father',
            'Friend',
            'Family',
        ],
    },
    sessions: {
        title: 'Styling sessions',
        add: 'Add styling session',
        empty: 'No styling sessions',
        emptyBody: 'Booked sessions and their assignments appear here.',
        columns: {
            session: 'Session',
            serviceType: 'Service type',
            appointment: 'Appointment',
            country: 'Country',
            assignedTo: 'Assigned to',
            due: 'Due date',
            images: 'Images',
            actions: 'Actions',
        },
        addedBy: (who: string | null, when: string) =>
            who ? `Added by ${who} · ${when}` : `Added ${when}`,
        images: (n: number) => (n === 0 ? '—' : n === 1 ? '1 image' : `${n} images`),
        view: (what: string) => `View ${what}`,
        edit: (what: string) => `Edit ${what}`,
        newTitle: 'New styling session',
        editTitle: 'Edit styling session',
        viewTitle: 'Styling session',
        forClient: (name: string, type: string | null) =>
            type ? `For ${name} · ${type}` : `For ${name}`,
        name: 'Session name',
        namePlaceholder: 'e.g. Met Gala fitting',
        serviceType: 'Service type',
        serviceTypePick: 'Select…',
        noServiceTypes: 'The service type register is empty, so there is nothing to choose yet.',
        appointment: 'Appointment date',
        country: 'Country',
        assignedTo: 'Assigned to',
        assignedToPick: 'Select a stylist…',
        noStylists: 'No active relationship managers, so there is nobody to assign to yet.',
        due: 'Due date',
        brief: 'Details',
        briefPlaceholder: 'What the client asked for, and anything the stylist needs to know.',
        noBrief: 'No brief recorded',
        references: 'Reference images',
        filesLabel: 'Files',
        countryPick: 'Country',
        countrySearch: 'Search countries…',
        countryNone: 'No country matches.',
        dropImages: 'Drop reference images here or browse',
        /*
          The accept list and the note beside it, together, because they drifted apart: the note
          read "JPG or PNG" while the control and the API's own magic-byte allowlist took four
          formats (ALS-188). The API is the authority, so the note moved rather than the list.
          styling.test.ts holds the two against each other.
        */
        imageAccept: 'image/png,image/jpeg,image/webp,image/gif',
        dropImagesHint: 'JPG, PNG, GIF or WebP, up to 40.',
        dropFiles: 'Drop files here or browse',
        fileAccept: 'application/pdf,.doc,.docx,.xls,.xlsx',
        dropFilesHint: 'PDF, Word or Excel.',
        none: 'None',
        removeFile: (what: string) => `Remove ${what}`,
        /** A legacy file: named, never linked, because it is not on our disk until cutover. */
        legacyFile: 'On the old portal until the files are moved across.',
        save: 'Create session',
        saveEdit: 'Save session',
        cancel: 'Cancel',
        close: 'Close',
        /** The corner X, which would otherwise be a second control called Close (ALS-188). */
        dismiss: 'Dismiss',
        editFromView: 'Edit session',
        readOnly: 'Read-only. Edit session opens the same record for changes.',
        footNote: "The session appears on the stylist's list as soon as you save.",
        markDone: 'Mark done',
        reopen: 'Reopen',
        closedBy: (who: string | null, when: string) =>
            who ? `Closed by ${who} · ${when}` : `Closed ${when}`,
        /** The line under the badge in the read-only view, which says the status in words. */
        headline: (status: StylingSessionStatus, due: string) =>
            status === 'Done'
                ? 'Closed by the stylist'
                : status === 'Overdue'
                  ? `Due ${due}, still open`
                  : `Due ${due}`,
        added: 'Added',
        failed: 'That session could not be saved.',
        uploadFailed: (names: string[]) =>
            names.length === 1
                ? `${names[0]} was refused.`
                : `${names.length} references were refused: ${names.join(', ')}.`,
    },
    /*
      The Wardrobe tab (ALS-52). The words are the prototype's where it has them. Two are
      deliberately not: the prototype's Locations card still offers ARCHIVING, which the ticket
      withdrew on 04 Sep, and its outfit tiles print an occasion that nothing in the database can
      yet supply.
    */
    wardrobe: {
        title: 'Wardrobe',
        add: 'Add piece',
        edit: (name: string) => `Edit ${name}`,
        remove: (name: string) => `Remove ${name}`,
        subtitle: (shown: number, total: number) =>
            shown === total
                ? `${total} ${total === 1 ? 'piece' : 'pieces'}`
                : `${shown} of ${total} pieces`,
        /*
          The pager's line. One page reads as the prototype draws it, "Showing 4 of 4 pieces";
          past that it is a range, as the Orders register's is, because "Showing 4 of 52" on
          the last page read as though only four pieces existed (ALS-226).
        */
        showing: (from: number, to: number, total: number) => {
            const noun = total === 1 ? 'piece' : 'pieces';
            const n = (value: number) => value.toLocaleString('en-GB');
            return to - from + 1 >= total
                ? `Showing ${n(total)} of ${n(total)} ${noun}`
                : `Showing ${n(from)}-${n(to)} of ${n(total)} ${noun}`;
        },
        pieceCount: (n: number) => `${n} ${n === 1 ? 'piece' : 'pieces'}`,
        empty: 'No pieces match',
        emptyBody: 'Nothing in the wardrobe matches the filters and source together.',
        emptyAction: 'Clear filters',
        /** A client with nothing in their wardrobe at all, which is a different thing. */
        none: 'Nothing in this wardrobe yet',
        noneBody:
            'Pieces arrive when an order is delivered, when the client photographs something in the app, or when the team records one here.',
        noLocation: 'No location',
        /** The pill on the card. All is not a source, which is why it is separate. */
        pills: {
            all: 'All',
            Order: 'From orders',
            SelfUpload: 'Self-uploads',
            ClientOwn: 'Client\u2019s own',
        },
        pillLabel: 'Filter by source',
        /** The badge under a tile. An order-sourced piece shows its order number instead. */
        sources: {
            Order: 'From an order',
            SelfUpload: 'Self-upload',
            ClientOwn: 'Client\u2019s own',
            /* A piece the team picked out of the catalogue. Its own badge because the product
               record owns it, exactly as an order line does, and the editor locks it the same
               way; the pill still counts it under "Client's own", which is who put it there. */
            Database: 'From database',
        },
        filters: {
            search: 'Search name or brand',
            searchChip: 'Search',
            searchPlaceholder: 'e.g. Toteme',
            productGroup: 'Product',
            productGroupNote: 'what the piece is',
            category: 'Category',
            categoryChip: 'Category',
            anyCategory: 'All categories',
            brand: 'Brand',
            brandChip: 'Brand',
            anyBrand: 'All brands',
            colour: 'Colour',
            colourChip: 'Colour',
            anyColour: 'All colours',
            size: 'Size',
            sizeChip: 'Size',
            anySize: 'All sizes',
            locationGroup: 'Location',
            locationGroupNote: 'where this client keeps it',
            locations: 'Locations',
            locationChip: 'Location',
            /** The drawer's own button, which names what the pending set would show. */
            apply: (n: number) => `Show ${n} ${n === 1 ? 'piece' : 'pieces'}`,
        },
        outfits: {
            title: 'Outfits',
            add: 'Add outfit',
            edit: (name: string) => `Edit ${name}`,
            count: (n: number) =>
                `${n} ${n === 1 ? 'outfit' : 'outfits'} \u00b7 pieces the client already owns`,
            empty: 'No outfits yet',
            emptyBody:
                'Outfits are compositions of wardrobe pieces, built in the app\u2019s Outfit Builder or here by the team.',
            /** "3 pieces · Gala", or "No pieces" for an outfit that holds none yet (ALS-233). */
            meta: (pieces: number, occasion: string | null) =>
                `${pieces === 0 ? 'No pieces' : `${pieces} ${pieces === 1 ? 'piece' : 'pieces'}`}${occasion ? ` \u00b7 ${occasion}` : ''}`,
            /** The tile's own name as a control: it opens the read-only view (ALS-233). */
            view: (name: string) => `View ${name}`,
            remove: (name: string) => `Remove ${name}`,
            /** The confirm names it and promises the pieces stay, which the API honours. */
            confirmTitle: 'Remove outfit?',
            confirmBody: (name: string, pieces: number) =>
                pieces === 0
                    ? `${name} is removed from this client. It holds no pieces.`
                    : `${name} is removed from this client. Its ${pieces} ${pieces === 1 ? 'piece stays' : 'pieces stay'} in the wardrobe.`,
            confirm: 'Remove',
            cancel: 'Cancel',
            removed: (name: string) =>
                `Removed \u00b7 ${name} \u00b7 the pieces stay in the wardrobe`,
            failed: 'That outfit could not be removed.',
        },
        locations: {
            title: 'Locations',
            add: 'Add location',
            count: (n: number) =>
                `${n} ${n === 1 ? 'location' : 'locations'} \u00b7 where the wardrobe lives`,
            empty: 'No locations yet',
            emptyBody:
                'Add the places this client keeps pieces and each piece can then say where it is.',
            edit: (name: string) => `Edit ${name}`,
            remove: (name: string) => `Delete ${name}`,
            name: 'Location name',
            namePlaceholder: 'e.g. London flat',
            note: 'Note',
            notePlaceholder: 'Optional. Which room, which rail.',
            required: 'Required.',
            addTitle: 'Add location',
            editTitle: 'Edit location',
            /** Renaming moves the pieces, so the drawer says so before Save rather than after. */
            renameNote: (n: number) =>
                n === 0
                    ? 'Nothing is recorded here yet.'
                    : `Renaming moves ${n === 1 ? 'the 1 piece' : `all ${n} pieces`} recorded here.`,
            save: 'Save',
            cancel: 'Cancel',
            added: (name: string) => `Added \u00b7 ${name}`,
            renamed: (name: string, moved: number) =>
                moved === 0
                    ? `Saved \u00b7 ${name}`
                    : `Renamed \u00b7 ${name} \u00b7 ${moved} ${moved === 1 ? 'piece' : 'pieces'} moved`,
            failed: 'That location could not be saved.',
            /** An empty location: a plain confirm, as the ticket asks. */
            deleteTitle: (name: string) => `Delete ${name}?`,
            deleteBody: 'Nothing is recorded here, so nothing moves.',
            deleteConfirm: 'Delete',
            /** One holding pieces: the same dialog, asking where they go, deleting in one step. */
            moveTitle: (name: string) => `Delete ${name}?`,
            moveBody: (n: number) =>
                `${n} ${n === 1 ? 'piece is' : 'pieces are'} recorded there. Choose where ${n === 1 ? 'it goes' : 'they go'} and the location is deleted.`,
            moveLabel: (n: number) => `Move ${n === 1 ? 'the piece' : 'the pieces'} to`,
            moveNowhere: 'No location',
            moveConfirm: 'Move and delete',
            deleted: (name: string) => `Deleted \u00b7 ${name}`,
            movedAndDeleted: (name: string, n: number, target: string) =>
                `Deleted \u00b7 ${name} \u00b7 ${n} ${n === 1 ? 'piece moved to' : 'pieces moved to'} ${target}`,
            deleteFailed: 'That location could not be deleted.',
        },
        /** The wardrobe read failing is its own state: the rest of the record is fine. */
        loadFailed: 'The wardrobe could not be loaded.',
        retry: 'Try again',

        /*
          The piece editor (ALS-53, frames 3144:70330 and 3144:70687). The words follow the
          prototype's side sheet; the locked note is the ticket's own wording, because it has to
          say WHY a field cannot be changed rather than just that it cannot.
        */
        piece: {
            addTitle: 'Add piece',
            editTitle: 'Edit piece',
            subtitle: (name: string) => `For ${name}`,
            addFrom: 'Add from',
            fromCatalogue: 'Product database',
            fromOwn: 'Client\u2019s own item',
            catalogueNote:
                'Adds the product with its catalogue details. For a piece the client owns that we do not stock, switch to their own item.',
            ownNote:
                'A piece the client owns that is not in our product database. It belongs to this wardrobe only.',
            search: 'Search the product database',
            searchPlaceholder: 'Search the product database\u2026',
            /** Under the catalogue list, beside its pager. */
            showing: (shown: number, total: number) =>
                `Showing ${shown} of ${total.toLocaleString('en-GB')} ${total === 1 ? 'product' : 'products'}`,
            /* The field's hint says what you may type; the empty results box says what it is
               waiting for. The same words in both read as a label with no answer under it. */
            searchPrompt: 'Start typing to search the product database.',
            /** The spoken name of the chip that names what is chosen; clicking it clears. */
            unpick: (name: string) => `Remove ${name}`,
            searching: 'Searching\u2026',
            noProducts: 'No products match.',
            /** Said on a locked piece, naming what owns it, which is the ticket's requirement. */
            lockedByOrder: (orderNo: string) =>
                `From order ${orderNo}. The product record owns the name, brand, category, colour, size and photo, so only the location changes here.`,
            /* Names the product, the way the order note names its order: ALS-53 asks the note to
               say WHICH record owns the fields, and the code is how the register is searched. A
               piece whose product carries no code still says where it came from (ALS-222). */
            /* Names only the four fields the product owns: the colour and size are this client's
               copy, like the location, and change here (ALS-215). */
            lockedByCatalogue: (code: string | null) =>
                `${code === null ? 'From the product database' : `From product ${code} in the database`}. The product record owns the name, brand, category and photo. The colour, size and location are this client\u2019s and change here.`,
            /** Beside the locked photo, saying where to change it rather than only that you cannot. */
            photoLocked: (owner: string) =>
                `From ${owner}. Changing this photo means changing the product, and every client\u2019s wardrobe follows.`,
            brandPlaceholder: 'Choose a brand',
            brandSearch: 'Search brands',
            brandNone: 'No brand matches.',
            photo: 'Photo',
            /* The four formats the API's magic-byte check takes (ImageUpload.NotAnImage); the note
               had said "JPG or PNG" (ALS-222, done in ALS-271). */
            photoHint:
                'JPG, PNG, WebP or GIF. Shown on the tile in place of the generated artwork.',
            photoDrop: 'Drop a photo, or click to choose',
            photoRemove: 'Remove photo',
            /** The alt text on the local preview of a photo chosen but not yet uploaded. */
            photoChosen: 'The photo you chose',
            /** Why a tile shows artwork rather than a picture, said once where it is asked about. */
            photoLegacy:
                'Photos taken in the app are still on the old system and arrive when it is switched off.',
            name: 'Product name',
            namePlaceholder: 'e.g. Eleonora silk-blend gown',
            brand: 'Brand',
            category: 'Category',
            /* The prototype pre-selects its first category, but a default on a required field
               files a piece under Dresses unnoticed, so it asks, as Brand does (ALS-295). */
            categoryPlaceholder: 'Choose a category',
            /* Not in the prototype's editor. The app files a wardrobe by it (Handbags, Upper
               Body...), so a piece without one shows under Other on the client's phone. Named as
               the product editor names the same register. */
            secondary: 'Secondary category',
            secondaryPlaceholder: 'Choose a secondary category',
            secondaryNeedsCategory: 'Choose a category first',
            secondaryShowHidden: 'Show hidden from app',
            /* Hidden keeps a secondary out of the app's own picker, not the piece off the phone:
               the app's wardrobe filters it on order lines only (ALS-303). */
            secondaryHidden:
                "Hidden from the app's lists, so the client can't choose it there. The piece still shows on their phone.",
            secondaryNoneShown:
                'No secondary in this category shows in the app. Tick Show hidden from app to choose one.',
            secondaryNone: 'This category has no secondary to choose. Add one in Product settings.',
            colour: 'Colour',
            size: 'Size',
            location: 'Location',
            noLocation: 'Not recorded',
            notes: 'Notes',
            notesPlaceholder: 'Anything a stylist should know about this piece.',
            save: 'Save piece',
            cancel: 'Cancel',
            required: 'Required.',
            added: (name: string) => `Added \u00b7 ${name}`,
            saved: (name: string) => `Saved \u00b7 ${name}`,
            failed: 'That piece could not be saved.',
            /* Says the reason the server gave rather than only that it failed, and does not claim
               a save that did not happen: on an edit nothing was being saved alongside the photo
               (ALS-222). The second half is only true while adding, so only added there. */
            photoFailed: (reason: string | null, adding: boolean) =>
                `${reason ?? 'That photo could not be uploaded.'}${adding ? ' The piece itself is saved.' : ''}`,
            photoSaved: 'Photo updated',
            photoRemoved: 'Photo removed',
            /* Refused before it is sent, which is what stops an oversized upload hanging with
               nothing on screen (ALS-217). Named in megabytes because that is what a file browser
               shows. */
            photoTooLarge: (limitMb: number) => `That photo is larger than ${limitMb} MB.`,
            /** The prototype's own words for the product gallery's Photoroom state, which is the one thing an upload now waits on. */
            photoUploading: 'Photoroom \u00b7 removing background\u2026',
            /* Background removal (ALS-54). Every new photo is cut out and the original kept, so the
               field says which of the two is showing and offers the other. */
            photoCutOutApplied: 'Background removed by Photoroom. The original is kept.',
            photoCutOutReverted: 'Showing the original. The cut-out is kept.',
            photoUseOriginal: 'Use original',
            photoUseCutOut: 'Use cut-out',
            photoCutOutFailed: 'That could not be changed.',
            /** Removing differs by source, and the confirm says which, which is the ticket's rule. */
            removeBoughtTitle: 'Remove from wardrobe?',
            removeBoughtBody: (name: string, orderNo: string | null) =>
                `${name} leaves this client\u2019s wardrobe.${orderNo ? ` Order ${orderNo} is not changed.` : ' The order history is not changed.'}`,
            removeBoughtConfirm: 'Remove from wardrobe',
            removeOwnTitle: 'Delete piece?',
            removeOwnBody: (name: string) => `${name} is deleted from this client\u2019s wardrobe.`,
            /* Appended to either confirm when the piece is in an outfit, in the prototype's words
               (ALS-233): the piece leaves the outfit and the outfit stays. Named up to three: one
               piece sits in 39 of one client's outfits, and a confirm that lists all of them is
               one nobody reads. */
            removeLeavesOutfits: (names: readonly string[]) => {
                if (names.length === 1)
                    return ` It also leaves the outfit ${names[0]}. The outfit stays.`;
                const shown = names.slice(0, 3).join(', ');
                const rest = names.length - 3;
                return ` It also leaves ${names.length} outfits: ${shown}${rest > 0 ? ` and ${rest} more` : ''}. The outfits stay.`;
            },
            removeOwnConfirm: 'Delete',
            /** The notice after, as the prototype words it, with the outfits it left (ALS-233). */
            removed: (name: string, bought: boolean, leftOutfits: number) =>
                (bought
                    ? `Removed from wardrobe \u00b7 ${name} \u00b7 order history preserved`
                    : `Deleted \u00b7 ${name}`) +
                (leftOutfits > 0
                    ? ` \u00b7 left ${leftOutfits} ${leftOutfits === 1 ? 'outfit' : 'outfits'}`
                    : ''),
            removeFailed: 'That piece could not be removed.',
        },

        /* The Add outfit editor (3144:70687): the wide sheet, with the picker over two sources. */
        outfitEditor: {
            addTitle: 'Add outfit',
            editTitle: 'Edit outfit',
            subtitle: (name: string) => `For ${name} \u00b7 built from pieces they own`,
            /** The right-hand column on a database row, where a wardrobe piece names its location. */
            fromDatabase: 'Database',
            cover: 'Cover photo',
            /** The outfit saved and its new cover refused, with the API's reason when it gave one (ALS-495). */
            coverRefused: (reason: string | null) =>
                `Outfit saved without the new cover.${reason === null ? '' : ` ${reason}`}`,
            coverDrop: 'Drop a photo here or browse',
            /** The drop box once a cover or a new photo is shown above it (ALS-410). */
            coverReplace: 'Replace with another photo, or browse',
            /** Drawn inside the drop box, as the wide editor's frame has it (3144:70687). */
            coverNote: 'Optional · the collage of pieces is used if you leave it empty',
            /* The cover tile (ALS-410): the stored cover carries Current, a newly picked photo
               says what it replaces when the drawer saves. */
            coverCurrent: 'Current',
            coverNew: (replacesCover: boolean) =>
                `New · replaces the ${replacesCover ? 'current cover' : 'collage'} on save`,
            coverDownload: (fileName: string) => `Download ${fileName}`,
            coverDownloadTitle: 'Download the cover',
            coverRemove: (fileName: string) => `Remove ${fileName}`,
            coverRemoveTitle: 'Remove',
            /* Removing waits for Save, as adding and replacing already did: Cancel has to undo
               every change the drawer made, and this one used to happen the moment it was clicked
               (ALS-222). Said where the tile was, with a way back (ALS-410). */
            coverRemoved: 'Cover removed · the collage of pieces is used after save.',
            coverUndo: 'Undo',
            coverTooLarge: (limitMb: number) => `That cover is larger than ${limitMb} MB.`,
            coverNotImage: 'The cover must be an image (JPEG, PNG or WebP).',
            name: 'Outfit name',
            namePlaceholder: 'e.g. Met season arrival',
            occasion: 'Occasion',
            occasionPlaceholder: 'e.g. Gala, Travel',
            pieces: 'Pieces in this outfit',
            /** The badge beside the pieces head, once something is picked. */
            selectedCount: (n: number) => `${n} selected`,
            /** The switch's spoken name, as the prototype's group names it. */
            sourceLabel: 'Product source',
            // The two segments as the prototype and ALS-233 name them.
            sourceWardrobe: 'Client wardrobe',
            sourceDatabase: 'Product database',
            searchWardrobe: 'Search this wardrobe',
            searchDatabase: 'Search the product database',
            /* The prototype's "Search her wardrobe…", without guessing a pronoun. */
            searchWardrobePlaceholder: 'Search the client\u2019s wardrobe\u2026',
            searchDatabasePlaceholder: 'Search the product database\u2026',
            /* The prototype shows six at a time and says how many there are altogether. The
               total is what could be found, not what fitted on the page: reading "6 of 8" against
               a catalogue of 17,425 was the page size wearing the word "of" (ALS-222). */
            showing: (shown: number, total: number) =>
                `Showing ${shown} of ${total.toLocaleString('en-GB')} ${total === 1 ? 'piece' : 'pieces'}`,
            noneWardrobe: 'No wardrobe pieces match.',
            noneDatabase: 'No database products match.',
            /** The footer counts what is picked; none is allowed, and says so (ALS-233). */
            picked: (n: number) =>
                n === 0 ? 'No pieces picked yet' : `${n} ${n === 1 ? 'piece' : 'pieces'} selected`,
            save: 'Save outfit',
            cancel: 'Cancel',
            required: 'Required.',
            added: (name: string) => `Added \u00b7 ${name}`,
            saved: (name: string, coverRemoved = false) =>
                `Saved \u00b7 ${name}${coverRemoved ? ' \u00b7 cover removed, the collage is used' : ''}`,
            failed: 'That outfit could not be saved.',
            /* dbo.WardrobeOutfit.OutfitName is varchar(100). Said on the field rather than left
               to the save, which answered a 101-character name with "One or more validation
               errors occurred." and nothing under the box (ALS-222). */
            tooLong: (limit: number) => `At most ${limit} characters.`,
        },

        /* The read-only outfit view (ALS-233): the same side sheet as the editor, with the outfit
           in it instead of a form. Opened from the tile; Edit outfit turns it into the editor. */
        outfitView: {
            subtitle: (client: string, occasion: string | null) =>
                `For ${client}${occasion ? ` \u00b7 ${occasion}` : ''}`,
            cover: 'Cover',
            name: 'Outfit name',
            occasion: 'Occasion',
            noOccasion: '\u2014',
            pieces: 'Pieces in this outfit',
            empty: 'No pieces yet. Edit the outfit to pick some.',
            /** A catalogue member's second line: it is in the outfit, not in the wardrobe. */
            notInWardrobe: 'Not in the wardrobe',
            count: (n: number) =>
                n === 0 ? 'No pieces in this outfit' : `${n} ${n === 1 ? 'piece' : 'pieces'}`,
            close: 'Close',
            edit: 'Edit outfit',
            /** The cover as a control, for the screen reader: it opens the preview (ALS-235). */
            previewCover: (name: string) => `Preview the cover of ${name}`,
        },

        /* The cover, large (ALS-235), in the shared preview window. */
        coverPreview: {
            /** "Cover photo · airport-look.jpg"; a cover uploaded before the name was kept has none. */
            photoSubtitle: (fileName: string | null) =>
                fileName ? `Cover photo \u00b7 ${fileName}` : 'Cover photo',
            photoAlt: (name: string) => `Cover of ${name}`,
            photoNote: 'The cover the client sees on this outfit in the app.',
            download: 'Download photo',
            collageSubtitle: (n: number) =>
                `Cover \u00b7 the collage of its ${n} ${n === 1 ? 'piece' : 'pieces'} (no photo uploaded)`,
            collageNote: 'Upload a cover photo in Edit outfit to replace the collage.',
            edit: 'Edit outfit',
            downloadFailed: 'That photo could not be downloaded.',
        },
    },
    create: {
        title: 'Add customer',
        subtitle: 'What approval and first contact need. The rest lives on the profile.',
        save: 'Save customer',
        cancel: 'Cancel',
        locationHint: 'Starts on the first location; change it if the client belongs elsewhere.',
        added: (name: string) => `Added · ${name}`,
        noRegister: 'This register is empty, so there is nothing to choose yet.',
        failed: 'That customer could not be added.',
    },
    /*
      The register's name for a membership that costs nothing (dbo.mCustomerMembership FREE), which
      the list leaves out of the Membership column rather than printing beside the chat.
    */
    freeMembership: 'Free',
    saved: (name: string) => `Saved · ${name}`,
    savedWithManager: (name: string, manager: string) => `Saved · ${name} · now with ${manager}`,
    saveFailed: 'The customer could not be saved. Your changes are still here.',
} as const;

/** The five platforms the design draws as chips, in its order. */
export const SOCIAL_PLATFORMS = [
    { key: 'instagram', label: 'Instagram' },
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'snapchat', label: 'Snapchat' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'x', label: 'X (Twitter)' },
] as const;

export type SocialKey = (typeof SOCIAL_PLATFORMS)[number]['key'];

/**
 * The badge each client status wears. dbo.mStatus module 401 holds four: the two that mean the
 * client is with us in some form (Active, Lapsed) and the two that mean they are not (InActive,
 * Disabled). Anything the register grows later falls back to the neutral badge rather than
 * claiming a meaning nobody has given it.
 *
 * <p>Keyed on the register's own spelling, which is "InActive" with a capital A. That is a
 * question open with the client (ALS-168); until it is answered, matching the database is the only
 * thing that keeps the badge from silently turning grey when somebody fixes it.</p>
 */
export const CLIENT_STATUS_VARIANT: Record<
    string,
    'success' | 'secondary' | 'warning' | 'destructive'
> = {
    Active: 'success',
    InActive: 'secondary',
    Inactive: 'secondary',
    Lapsed: 'warning',
    Disabled: 'destructive',
};

export function clientStatusVariant(
    status: string,
): 'success' | 'secondary' | 'warning' | 'destructive' {
    return CLIENT_STATUS_VARIANT[status] ?? 'secondary';
}

/**
 * How a client status is spelt on screen.
 *
 * <p>dbo.mStatus CU-02 is stored as "InActive", with a capital A, and that is the value everything
 * compares and filters on: the register row, the API, the sort, the filter key. This maps it for
 * reading only, because the misspelling is the database's and correcting it there is a data change
 * the client has to agree to (raised on ALS-168). Every other status is spelt correctly already and
 * passes through untouched.</p>
 */
const CLIENT_STATUS_LABEL: Record<string, string> = { InActive: 'Inactive' };

export function clientStatusLabel(status: string): string {
    return CLIENT_STATUS_LABEL[status] ?? status;
}
